import { PostDTO, UserDTO } from "types";
import { getImageUrl } from "utils/image.util";

import { ReactComponent as EmptyHeartIcon } from "assets/images/empty.heart.svg";
import { ReactComponent as FillHeartIcon } from "assets/images/fill.heart.svg";
import { ReactComponent as ExportIcon } from "assets/images/export.svg";
import {
  Loading,
} from "components";
import { getPostLike, getPostLikeCount, patchPostLike } from "apis";
import { useEffect, useState } from "react";
import { useAlertStore, useUserStore } from "store";

interface PostDetailProps {
  post?: PostDTO;
  like?: boolean;
}

interface PostHeaderProps {
  user: UserDTO;
}

const PostHeader = ({ user }: PostHeaderProps) => {
  return (
    <div className="flex items-center space-x-2">
      {user.profileImage ? (
        <img
          src={getImageUrl(user.profileImage)}
          alt="profile"
          className="w-8 h-8 rounded-full object-cover object-center"
        />
      ) : (
        <div className="w-8 h-8 bg-achromatic-100 rounded-full"></div>
      )}
      <div className="flex flex-col">
        <div className="text-black leading-[14px] font-semibold">
          {user.name}
        </div>
        <div className="text-achromatic-300 leading-[14px]">
          @{user.profileId}
        </div>
      </div>
    </div>
  );
};

const PostFooter = ({ post, like }: PostDetailProps) => {
  const [isLike, setLike] = useState<boolean>(like!);
  const [likeCount, setLikeCount] = useState<number>(post!.postLikes);
  const [animate, setAnimate] = useState<boolean>(false);
  const showAlert = useAlertStore((state) => state.showAlert);

  const handleClickPostLike = async (postId: string) => {
    if (window.location.pathname.startsWith('/admin')) return;

    const newLikeState = !isLike;
    setLike(newLikeState);
    setAnimate(true);

    setTimeout(() => {
      setAnimate(false);
    }, 150);

    try {
      const response = await patchPostLike(postId);
      if (response.data.isSuccess !== "T") {
        throw new Error("Failed to update like");
      }
      const countResponse = await getPostLikeCount(postId);
      setLikeCount(countResponse.data.data);
    } catch (error) {
      console.error("좋아요 업데이트 중 오류 발생:", error);
      showAlert("좋아요를 업데이트하는 데 실패했습니다.", "error");
      setLike(!newLikeState);
      setAnimate(false);
    }
  };

  const handleExportClick = () => {
    if (window.location.pathname.startsWith('/admin')) return;

    navigator.clipboard.writeText(window.location.href + "post/" + post!.id);
    showAlert("클립보드에 복사되었습니다.", 'success');
  }

  const fetchInitialLikeState = async () => {
    try {
      const response = await getPostLike(post!.id);
      if (response.data.isSuccess === "T") {
        setLike(response.data.data);
      }
    } catch (error) {
      console.error("초기 좋아요 상태를 가져오는 데 실패했습니다.", error);
    }
  };

  useEffect(() => {
    fetchInitialLikeState();
  }, [post]);

  return (
    <div className="flex justify-between items-center">
      <div className="flex items-center space-x-2">
        <div
          className="text-achromatic-300 cursor-pointer"
          onClick={() => handleClickPostLike(post!.id)}
        >
          {isLike ? (
            <div
              className={`flex items-center gap-1 transform transition-transform duration-300 ease-in-out ${animate ? "scale-110" : ""
                }`}
            >
              <FillHeartIcon className="w-6 h-6" />
              <span className="text-[#F04D1A] font-semibold text-sm">
                좋아요 {likeCount}
              </span>
            </div>
          ) : (
            <div
              className={`flex items-center gap-1 transform transition-transform duration-300 ease-in-out ${animate ? "scale-90" : ""
                }`}
            >
              <EmptyHeartIcon className="w-6 h-6" />
              <span className="text-achromatic-300 font-semibold text-sm">
                좋아요 {likeCount}
              </span>
            </div>
          )}
        </div>
      </div>
      <div
        className="text-achromatic-300 cursor-pointer"
        onClick={handleExportClick}
      >
        <ExportIcon className="w-6 h-6" />
      </div>
    </div>
  );
};

const PostDetailComponent = ({ post, like }: PostDetailProps) => {
  const removeHashtags = (content: string) => {
    return content.replace(/#\S+/g, "").trim();
  };

  const formatHashtags = (hashtags: string) => {
    if (!hashtags) return "";
    return hashtags
      .split(",")
      .map((tag) => `#${tag.trim()}`)
      .join(" ");
  };

  if (!post) {
    return <Loading />;
  }

  return (
    <div className="h-full">
      <div className="flex flex-col h-full gap-8 rounded-2xl border-achromatic-100 border p-6 mobile:border-0">
        <div className="flex flex-col w-full h-full text-sm font-medium">
          <PostHeader user={post.postWriter} />
          <div className="pt-4">
            <p>{removeHashtags(post.content)}</p>
            &nbsp;
            <p className="text-primary-500">
              {formatHashtags(post!.hashtags)}
            </p>
          </div>
          <div>
            <PostFooter post={post} like={like} />
          </div>
        </div>
      </div>
    </div>
  );
};

export default PostDetailComponent;
